import { db, auth, storage } from '../firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  startAfter,
  serverTimestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { getPlatformFee, getSellerAmount } from '../constants/monetization';

const MAX_CHAT_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10MB

/**
 * Subir imagen de referencia a Firebase Storage
 * @param {File} file - Archivo de imagen
 * @param {string} orderId - ID temporal de la orden
 * @returns {Promise<string>} URL de la imagen
 */
export const uploadReferenceImage = async (file, orderId) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('No autenticado');
    
    const fileName = `${uuidv4()}_${file.name}`;
    const storageRef = ref(storage, `order-references/${currentUser.uid}/${orderId}/${fileName}`);
    
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    return downloadURL;
  } catch (error) {
    console.error('Error al subir imagen de referencia:', error);
    throw error;
  }
};

/**
 * Subir un adjunto para el chat de una orden (imagen o archivo).
 * Cliente o vendedor pueden subir adjuntos en su chat.
 * @param {string} orderId - ID de la orden
 * @param {File} file - Archivo (imagen, PDF, etc.)
 * @returns {Promise<{ success: boolean, attachment?: { url: string, name: string, type: string, size: number }, error?: string }>}
 */
export const uploadOrderChatAttachment = async (orderId, file) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return { success: false, error: 'No autenticado' };
    if (!orderId) return { success: false, error: 'orderId requerido' };
    if (!file) return { success: false, error: 'Archivo requerido' };
    if (typeof file.size === 'number' && file.size > MAX_CHAT_ATTACHMENT_BYTES) {
      return { success: false, error: 'El archivo excede el límite de 10MB' };
    }

    const orderRef = doc(db, 'ServiceOrders', orderId);
    const orderDoc = await getDoc(orderRef);
    if (!orderDoc.exists()) return { success: false, error: 'Orden no encontrada' };

    const orderData = orderDoc.data() || {};
    const isClient = orderData.clientId === currentUser.uid;
    const isSeller = orderData.sellerId === currentUser.uid;
    if (!isClient && !isSeller) return { success: false, error: 'No tienes permiso para subir archivos a esta orden' };

    const safeName = (file.name || 'archivo').replace(/[^a-zA-Z0-9._-]/g, '_');
    const fileId = `${uuidv4()}_${safeName}`;
    const storageRef = ref(storage, `order-chat-attachments/${orderId}/${currentUser.uid}/${fileId}`);

    await uploadBytes(storageRef, file, { contentType: file.type || 'application/octet-stream' });
    const downloadURL = await getDownloadURL(storageRef);

    return {
      success: true,
      attachment: {
        url: downloadURL,
        name: file.name || safeName,
        type: file.type || 'application/octet-stream',
        size: typeof file.size === 'number' ? file.size : 0
      }
    };
  } catch (error) {
    console.error('Error al subir adjunto de chat:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Subir un archivo entregable (imagen o cualquier archivo) para una orden
 * Solo el vendedor debe llamar esto; los archivos se guardan sin compresión para no perder calidad
 * @param {string} orderId - ID de la orden
 * @param {File} file - Archivo (imagen, PDF, etc.)
 * @returns {Promise<{ success: boolean, url?: string, name?: string, error?: string }>}
 */
export const uploadDeliverableFile = async (orderId, file) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return { success: false, error: 'No autenticado' };

    const orderRef = doc(db, 'ServiceOrders', orderId);
    const orderDoc = await getDoc(orderRef);
    if (!orderDoc.exists()) return { success: false, error: 'Orden no encontrada' };
    const orderData = orderDoc.data();
    if (orderData.sellerId !== currentUser.uid) return { success: false, error: 'Solo el vendedor puede subir entregables' };

    const safeName = (file.name || 'archivo').replace(/[^a-zA-Z0-9._-]/g, '_');
    const fileId = `${uuidv4()}_${safeName}`;
    const storageRef = ref(storage, `order-deliverables/${orderId}/${fileId}`);

    await uploadBytes(storageRef, file, { contentType: file.type || 'application/octet-stream' });
    const downloadURL = await getDownloadURL(storageRef);

    return { success: true, url: downloadURL, name: file.name || safeName };
  } catch (error) {
    console.error('Error al subir entregable:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Añadir entregables a una orden (URLs ya subidas)
 * Solo el vendedor puede añadir entregables
 * @param {string} orderId - ID de la orden
 * @param {Array<{ url: string, name: string }>} newFiles - Lista de { url, name }
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const addDeliverablesToOrder = async (orderId, newFiles) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return { success: false, error: 'No autenticado' };
    if (!newFiles?.length) return { success: true };

    const orderRef = doc(db, 'ServiceOrders', orderId);
    const orderDoc = await getDoc(orderRef);
    if (!orderDoc.exists()) return { success: false, error: 'Orden no encontrada' };
    const orderData = orderDoc.data();
    if (orderData.sellerId !== currentUser.uid) return { success: false, error: 'Solo el vendedor puede añadir entregables' };

    const current = orderData.deliverables || [];
    const updated = [...current, ...newFiles];

    await updateDoc(orderRef, {
      deliverables: updated,
      updatedAt: serverTimestamp()
    });

    const { createNotification } = await import('./notificationService');
    await createNotification({
      userId: orderData.clientId,
      type: 'order_deliverables_added',
      payload: {
        orderId,
        sellerId: orderData.sellerId,
        sellerName: orderData.sellerName,
        serviceName: orderData.serviceName || null,
        fileCount: newFiles.length
      }
    });

    return { success: true };
  } catch (error) {
    console.error('Error al añadir entregables:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Eliminar un entregable de una orden por índice (solo vendedor)
 * @param {string} orderId - ID de la orden
 * @param {number} index - Índice del archivo en el array deliverables
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const removeDeliverableFromOrder = async (orderId, index) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return { success: false, error: 'No autenticado' };

    const orderRef = doc(db, 'ServiceOrders', orderId);
    const orderDoc = await getDoc(orderRef);
    if (!orderDoc.exists()) return { success: false, error: 'Orden no encontrada' };
    const orderData = orderDoc.data();
    if (orderData.sellerId !== currentUser.uid) return { success: false, error: 'Solo el vendedor puede eliminar entregables' };

    const current = orderData.deliverables || [];
    if (index < 0 || index >= current.length) return { success: false, error: 'Índice inválido' };

    const updated = current.filter((_, i) => i !== index);

    await updateDoc(orderRef, {
      deliverables: updated,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error al eliminar entregable:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Crear una nueva orden de servicio
 * @param {Object} orderData - Datos de la orden
 * @returns {Promise<Object>} Resultado de la operación
 */
export const createServiceOrder = async (orderData) => {
  try {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return {
        success: false,
        error: 'Debes iniciar sesión para solicitar un servicio'
      };
    }

    // Validaciones
    if (!orderData.serviceId || !orderData.sellerId) {
      return {
        success: false,
        error: 'Datos del servicio incompletos'
      };
    }

    if (!orderData.packageType || !orderData.amount) {
      return {
        success: false,
        error: 'Debes seleccionar un paquete'
      };
    }

    if (!orderData.description || orderData.description.trim() === '') {
      return {
        success: false,
        error: 'Debes proporcionar una descripción de lo que necesitas'
      };
    }

    // Obtener datos del usuario actual (cliente)
    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
    const userData = userDoc.exists() ? userDoc.data() : {};

    // Obtener datos del vendedor
    const sellerDoc = await getDoc(doc(db, 'users', orderData.sellerId));
    const sellerData = sellerDoc.exists() ? sellerDoc.data() : {};

    // Crear la orden
    const order = {
      // Referencias
      serviceId: orderData.serviceId,
      serviceName: orderData.serviceName || 'Servicio',
      serviceImage: orderData.serviceImage || null,
      
      // Usuarios
      clientId: currentUser.uid,
      clientName: userData.displayName || userData.username || 'Usuario',
      clientEmail: currentUser.email,
      sellerId: orderData.sellerId,
      sellerName: sellerData.displayName || sellerData.username || 'Vendedor',
      sellerEmail: sellerData.email || null,
      
      // Detalles del pedido
      packageType: orderData.packageType,
      packageName: orderData.packageName,
      amount: parseFloat(orderData.amount),
      currency: orderData.currency || 'ARS',
      // Monetización: cuando PLATFORM_COMMISSION_RATE > 0 y haya pasarela de pago, se usarán estos campos
      platformFee: getPlatformFee(parseFloat(orderData.amount)),
      sellerPayout: getSellerAmount(parseFloat(orderData.amount)),
      
      // Descripción del cliente
      description: orderData.description,
      deadline: orderData.deadline || null,
      referenceFiles: orderData.referenceFiles || [],
      
      // Estado
      status: 'pending',
      
      // Pago
      paymentId: null,
      paymentStatus: 'pending',
      paidAt: null,
      
      // Entregables
      deliverables: [],
      deliveredAt: null,
      
      // Mensajes
      messages: [],
      unreadByClient: false,
      unreadBySeller: false,
      lastMessageAt: null,
      lastMessageFrom: null,
      
      // Timestamps
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      acceptedAt: null,
      startedAt: null,
      completedAt: null,
      cancelledAt: null
    };

    console.log('📝 Creando orden de servicio...');
    const orderRef = await addDoc(collection(db, 'ServiceOrders'), order);
    
    console.log('✅ Orden creada:', orderRef.id);

    // Notificación in-app al vendedor (nuevo pedido de servicio)
    const { createNotification } = await import('./notificationService');
    await createNotification({
      userId: order.sellerId,
      type: 'order_request',
      payload: {
        orderId: orderRef.id,
        clientId: order.clientId,
        clientName: order.clientName,
        serviceName: order.serviceName
      }
    });

    return {
      success: true,
      orderId: orderRef.id,
      order: {
        id: orderRef.id,
        ...order,
        createdAt: new Date(),
        updatedAt: new Date()
      }
    };
  } catch (error) {
    console.error('Error al crear orden:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Obtener órdenes como comprador (cliente)
 * @param {string} userId - ID del usuario
 * @param {number} limitCount - Cantidad de órdenes a obtener
 * @param {Object} lastVisible - Último documento para paginación
 * @returns {Promise<Object>}
 */
/**
 * Obtener cantidad de compras (órdenes como cliente)
 * @param {string} userId - ID del usuario
 * @returns {Promise<{ success: boolean, count?: number, error?: string }>}
 */
export const getPurchasesCount = async (userId) => {
  try {
    const ordersRef = collection(db, 'ServiceOrders');
    const q = query(
      ordersRef,
      where('clientId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(500)
    );
    const snapshot = await getDocs(q);
    return { success: true, count: snapshot.size };
  } catch (error) {
    console.error('Error al contar compras:', error);
    return { success: false, error: error.message, count: 0 };
  }
};

/**
 * Obtener cantidad de ventas (órdenes como vendedor)
 * @param {string} userId - ID del usuario
 * @returns {Promise<{ success: boolean, count?: number, error?: string }>}
 */
export const getSalesCount = async (userId) => {
  try {
    const ordersRef = collection(db, 'ServiceOrders');
    const q = query(
      ordersRef,
      where('sellerId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(500)
    );
    const snapshot = await getDocs(q);
    return { success: true, count: snapshot.size };
  } catch (error) {
    console.error('Error al contar ventas:', error);
    return { success: false, error: error.message, count: 0 };
  }
};

/**
 * Analíticas de ventas: servicios terminados y total ganado (solo órdenes completadas)
 * @param {string} userId - ID del vendedor
 * @returns {Promise<{ success: boolean, completedCount?: number, totalEarned?: number, currency?: string, error?: string }>}
 */
export const getSalesAnalytics = async (userId) => {
  try {
    const ordersRef = collection(db, 'ServiceOrders');
    const q = query(
      ordersRef,
      where('sellerId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(500)
    );
    const snapshot = await getDocs(q);
    const completed = snapshot.docs.filter(d => d.data().status === 'completed');
    const completedCount = completed.length;
    let totalEarned = 0;
    let currency = 'ARS';
    completed.forEach(d => {
      const data = d.data();
      totalEarned += Number(data.amount) || 0;
      if (data.currency) currency = data.currency;
    });
    return { success: true, completedCount, totalEarned, currency };
  } catch (error) {
    console.error('Error al obtener analíticas de ventas:', error);
    return { success: false, completedCount: 0, totalEarned: 0, error: error.message };
  }
};

export const getPurchases = async (userId, limitCount = 20, lastVisible = null) => {
  try {
    const ordersRef = collection(db, 'ServiceOrders');
    let q;

    if (lastVisible) {
      q = query(
        ordersRef,
        where('clientId', '==', userId),
        orderBy('createdAt', 'desc'),
        startAfter(lastVisible),
        limit(limitCount)
      );
    } else {
      q = query(
        ordersRef,
        where('clientId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
    }

    const snapshot = await getDocs(q);
    const orders = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return {
      success: true,
      orders,
      lastVisible: snapshot.docs[snapshot.docs.length - 1] || null,
      hasMore: snapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('Error al obtener compras:', error);
    return {
      success: false,
      error: error.message,
      orders: []
    };
  }
};

/**
 * Obtener órdenes como vendedor (seller)
 * @param {string} userId - ID del usuario
 * @param {number} limitCount - Cantidad de órdenes a obtener
 * @param {Object} lastVisible - Último documento para paginación
 * @returns {Promise<Object>}
 */
export const getSales = async (userId, limitCount = 20, lastVisible = null) => {
  try {
    const ordersRef = collection(db, 'ServiceOrders');
    let q;

    if (lastVisible) {
      q = query(
        ordersRef,
        where('sellerId', '==', userId),
        orderBy('createdAt', 'desc'),
        startAfter(lastVisible),
        limit(limitCount)
      );
    } else {
      q = query(
        ordersRef,
        where('sellerId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
    }

    const snapshot = await getDocs(q);
    const orders = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return {
      success: true,
      orders,
      lastVisible: snapshot.docs[snapshot.docs.length - 1] || null,
      hasMore: snapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('Error al obtener ventas:', error);
    return {
      success: false,
      error: error.message,
      orders: []
    };
  }
};

/**
 * Obtener una orden específica
 * @param {string} orderId - ID de la orden
 * @returns {Promise<Object>}
 */
export const getOrder = async (orderId) => {
  try {
    const orderDoc = await getDoc(doc(db, 'ServiceOrders', orderId));
    
    if (!orderDoc.exists()) {
      return {
        success: false,
        error: 'Orden no encontrada'
      };
    }

    return {
      success: true,
      order: {
        id: orderDoc.id,
        ...orderDoc.data()
      }
    };
  } catch (error) {
    console.error('Error al obtener orden:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Actualizar el estado de una orden
 * @param {string} orderId - ID de la orden
 * @param {string} newStatus - Nuevo estado
 * @param {Object} additionalData - Datos adicionales a actualizar
 * @returns {Promise<Object>}
 */
export const updateOrderStatus = async (orderId, newStatus, additionalData = {}) => {
  try {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return {
        success: false,
        error: 'Debes iniciar sesión'
      };
    }

    const orderRef = doc(db, 'ServiceOrders', orderId);
    const orderDoc = await getDoc(orderRef);

    if (!orderDoc.exists()) {
      return {
        success: false,
        error: 'Orden no encontrada'
      };
    }

    const orderData = orderDoc.data();
    
    // Validar permisos
    const isClient = orderData.clientId === currentUser.uid;
    const isSeller = orderData.sellerId === currentUser.uid;

    if (!isClient && !isSeller) {
      return {
        success: false,
        error: 'No tienes permiso para modificar esta orden'
      };
    }

    // Validar transiciones de estado permitidas
    const allowedTransitions = {
      pending: ['accepted', 'cancelled'],
      accepted: ['payment_requested', 'in_progress', 'cancelled'],
      payment_requested: ['in_progress', 'cancelled'],
      in_progress: ['completed', 'cancelled'],
      completed: [],
      cancelled: []
    };

    if (!allowedTransitions[orderData.status]?.includes(newStatus)) {
      return {
        success: false,
        error: `No se puede cambiar de ${orderData.status} a ${newStatus}`
      };
    }

    // Preparar datos de actualización
    const updateData = {
      status: newStatus,
      updatedAt: serverTimestamp(),
      ...additionalData
    };

    // Agregar timestamps específicos según el estado
    if (newStatus === 'accepted') {
      updateData.acceptedAt = serverTimestamp();
    } else if (newStatus === 'payment_requested') {
      updateData.paymentRequestedAt = serverTimestamp();
    } else if (newStatus === 'in_progress') {
      updateData.startedAt = serverTimestamp();
    } else if (newStatus === 'completed') {
      updateData.completedAt = serverTimestamp();
    } else if (newStatus === 'cancelled') {
      updateData.cancelledAt = serverTimestamp();
    }

    await updateDoc(orderRef, updateData);

    console.log(`✅ Orden ${orderId} actualizada a ${newStatus}`);

    // Notificaciones in-app al cliente
    const { createNotification } = await import('./notificationService');
    if (newStatus === 'accepted' && isSeller) {
      await createNotification({
        userId: orderData.clientId,
        type: 'order_accepted',
        payload: {
          orderId,
          sellerId: orderData.sellerId,
          sellerName: orderData.sellerName
        }
      });
    } else if (newStatus === 'payment_requested' && isSeller) {
      await createNotification({
        userId: orderData.clientId,
        type: 'order_payment_requested',
        payload: {
          orderId,
          sellerId: orderData.sellerId,
          sellerName: orderData.sellerName,
          serviceName: orderData.serviceName || null,
          amount: orderData.amount ?? null,
          currency: orderData.currency || null
        }
      });
    } else if (newStatus === 'in_progress' && isSeller) {
      await createNotification({
        userId: orderData.clientId,
        type: 'order_in_progress',
        payload: {
          orderId,
          sellerId: orderData.sellerId,
          sellerName: orderData.sellerName,
          serviceName: orderData.serviceName || null
        }
      });
    } else if (newStatus === 'completed' && isSeller) {
      await createNotification({
        userId: orderData.clientId,
        type: 'order_completed',
        payload: {
          orderId,
          sellerId: orderData.sellerId,
          sellerName: orderData.sellerName,
          serviceName: orderData.serviceName || null
        }
      });
    } else if (newStatus === 'cancelled' && isSeller) {
      await createNotification({
        userId: orderData.clientId,
        type: 'order_rejected',
        payload: {
          orderId,
          sellerId: orderData.sellerId,
          sellerName: orderData.sellerName,
          reason: additionalData.reason || null
        }
      });
    }

    return {
      success: true,
      order: {
        id: orderId,
        ...orderData,
        ...updateData
      }
    };
  } catch (error) {
    console.error('Error al actualizar orden:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Agregar un mensaje a una orden
 * @param {string} orderId - ID de la orden
 * @param {string} message - Mensaje a agregar
 * @param {Array<{ url: string, name: string, type?: string, size?: number }>} attachments - Adjuntos del mensaje (opcional)
 * @returns {Promise<Object>}
 */
export const addOrderMessage = async (orderId, message, attachments = []) => {
  try {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return {
        success: false,
        error: 'Debes iniciar sesión'
      };
    }

    const orderRef = doc(db, 'ServiceOrders', orderId);
    const orderDoc = await getDoc(orderRef);

    if (!orderDoc.exists()) {
      return {
        success: false,
        error: 'Orden no encontrada'
      };
    }

    const orderData = orderDoc.data();
    const isClient = orderData.clientId === currentUser.uid;
    const isSeller = orderData.sellerId === currentUser.uid;

    if (!isClient && !isSeller) {
      return {
        success: false,
        error: 'No tienes permiso para agregar mensajes a esta orden'
      };
    }

    const safeAttachments = Array.isArray(attachments)
      ? attachments
          .filter((a) => a && typeof a === 'object' && typeof a.url === 'string')
          .map((a) => ({
            url: a.url,
            name: a.name || 'archivo',
            type: a.type || 'application/octet-stream',
            size: typeof a.size === 'number' ? a.size : 0
          }))
      : [];

    const newMessage = {
      from: isClient ? 'client' : 'seller',
      message: message,
      timestamp: new Date(),
      read: false,
      ...(safeAttachments.length > 0 ? { attachments: safeAttachments } : {})
    };

    const currentMessages = orderData.messages || [];
    
    await updateDoc(orderRef, {
      messages: [...currentMessages, newMessage],
      updatedAt: serverTimestamp(),
      lastMessageAt: serverTimestamp(),
      lastMessageFrom: newMessage.from,
      unreadByClient: newMessage.from === 'seller',
      unreadBySeller: newMessage.from === 'client'
    });

    // La notificación de chat se crea/actualiza en Cloud Function onOrderMessage (trigger al actualizar la orden)

    return {
      success: true,
      message: newMessage
    };
  } catch (error) {
    console.error('Error al agregar mensaje:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Marcar el chat como leído para el usuario actual (según rol).
 * @param {string} orderId - ID de la orden
 * @param {'client'|'seller'} viewAs - Rol actual en el modal/lista
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const markOrderChatAsRead = async (orderId, viewAs) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return { success: false, error: 'Debes iniciar sesión' };

    const orderRef = doc(db, 'ServiceOrders', orderId);
    const orderDoc = await getDoc(orderRef);
    if (!orderDoc.exists()) return { success: false, error: 'Orden no encontrada' };
    const orderData = orderDoc.data() || {};

    const isClient = orderData.clientId === currentUser.uid;
    const isSeller = orderData.sellerId === currentUser.uid;
    if (!isClient && !isSeller) return { success: false, error: 'No tienes permiso' };

    // viewAs define cómo se muestra el modal; igual validamos que coincida con el usuario.
    if (viewAs === 'client' && !isClient) return { success: false, error: 'Rol inválido' };
    if (viewAs === 'seller' && !isSeller) return { success: false, error: 'Rol inválido' };

    const update =
      viewAs === 'client'
        ? { unreadByClient: false, updatedAt: serverTimestamp() }
        : { unreadBySeller: false, updatedAt: serverTimestamp() };

    await updateDoc(orderRef, update);
    return { success: true };
  } catch (error) {
    console.error('Error al marcar chat como leído:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Cancelar una orden
 * @param {string} orderId - ID de la orden
 * @param {string} reason - Razón de la cancelación
 * @returns {Promise<Object>}
 */
export const cancelOrder = async (orderId, reason = '') => {
  return updateOrderStatus(orderId, 'cancelled', {
    cancellationReason: reason
  });
};

/**
 * Rechazar una solicitud (solo vendedor): notifica al cliente y elimina la orden.
 * @param {string} orderId - ID de la orden
 * @returns {Promise<Object>}
 */
export const rejectOrder = async (orderId) => {
  try {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      return { success: false, error: 'Debes iniciar sesión' };
    }

    const orderRef = doc(db, 'ServiceOrders', orderId);
    const orderDoc = await getDoc(orderRef);

    if (!orderDoc.exists()) {
      return { success: false, error: 'Orden no encontrada' };
    }

    const orderData = orderDoc.data();

    if (orderData.sellerId !== currentUser.uid) {
      return { success: false, error: 'No tienes permiso para rechazar esta solicitud' };
    }

    if (orderData.status !== 'pending') {
      return { success: false, error: 'Solo se pueden rechazar solicitudes pendientes' };
    }

    // Notificar al cliente antes de eliminar
    const { createNotification } = await import('./notificationService');
    await createNotification({
      userId: orderData.clientId,
      type: 'order_rejected',
      payload: {
        orderId,
        sellerId: orderData.sellerId,
        sellerName: orderData.sellerName
      }
    });

    await deleteDoc(orderRef);

    return { success: true };
  } catch (error) {
    console.error('Error al rechazar orden:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Eliminar una orden manualmente (solo vendedor). Uso temporal/limpieza.
 * @param {string} orderId - ID de la orden
 * @returns {Promise<Object>}
 */
export const deleteOrder = async (orderId) => {
  try {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      return { success: false, error: 'Debes iniciar sesión' };
    }

    const orderRef = doc(db, 'ServiceOrders', orderId);
    const orderDoc = await getDoc(orderRef);

    if (!orderDoc.exists()) {
      return { success: false, error: 'Orden no encontrada' };
    }

    const orderData = orderDoc.data();

    if (orderData.sellerId !== currentUser.uid) {
      return { success: false, error: 'Solo el vendedor puede eliminar esta orden' };
    }

    await deleteDoc(orderRef);
    return { success: true };
  } catch (error) {
    console.error('Error al eliminar orden:', error);
    return { success: false, error: error.message };
  }
};
